import { useState } from 'react';
import { Card, CardContent, CardHeader, Button } from '@etherisc/ui-kit';
import { 
  ArrowLeft, 
  MapPin, 
  Clock, 
  Database, 
  BarChart3, 
  Shield, 
  Link2,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Info
} from 'lucide-react';
import type { 
  CoverageDetailView,
  DataQualityLevel,
  DataQualityIssue,
  CoverageStatistics,
  SpatialInformation,
  TemporalInformation,
  DataQualityInfo,
  CoverageMetadata,
  RelatedDataInfo
} from '../../types/coverage';

interface CoverageDetailedViewProps {
  coverage: CoverageDetailView | null;
  loading?: boolean;
  error?: string | null;
  onBack?: () => void;
  className?: string;
}

export function CoverageDetailedView({
  coverage,
  loading = false,
  error = null,
  onBack,
  className = ''
}: CoverageDetailedViewProps) {
  const [activeTab, setActiveTab] = useState<string>('overview');

  if (error) {
    return (
      <Card className={`shadow-lg ${className}`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-red-600">
            <AlertTriangle className="w-6 h-6 mr-2" />
            <span>Error loading coverage details: {error}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <div className={`space-y-6 animate-pulse ${className}`}>
        <div className="h-8 bg-gray-200 rounded"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="h-64 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
          <div className="space-y-6">
            <div className="h-48 bg-gray-200 rounded"></div>
            <div className="h-32 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!coverage) {
    return (
      <Card className={`shadow-lg ${className}`}>
        <CardContent className="p-6">
          <div className="text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No coverage data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Info },
    { id: 'spatial', label: 'Spatial Info', icon: MapPin },
    { id: 'temporal', label: 'Temporal Info', icon: Clock },
    { id: 'quality', label: 'Data Quality', icon: Shield },
    { id: 'statistics', label: 'Statistics', icon: BarChart3 },
    { id: 'metadata', label: 'Metadata', icon: Database },
    { id: 'related', label: 'Related Data', icon: Link2 }
  ];

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {onBack && (
            <Button onClick={onBack} variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{coverage.basic.name}</h1>
            <p className="text-gray-600">{coverage.basic.description}</p>
          </div>
        </div>
        <StatusBadge status={coverage.basic.status} />
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <QuickStatCard
          label="Coverage Type"
          value={coverage.basic.coverage_type}
          icon={<Database className="w-5 h-5" />}
        />
        <QuickStatCard
          label="Data Source"
          value={coverage.basic.data_source}
          icon={<Database className="w-5 h-5" />}
        />
        <QuickStatCard
          label="Region"
          value={coverage.basic.region_name || coverage.basic.region_id}
          icon={<MapPin className="w-5 h-5" />}
        />
        <QuickStatCard
          label="Quality"
          value={coverage.dataQuality.overall}
          icon={<Shield className="w-5 h-5" />}
          valueColor={getQualityColor(coverage.dataQuality.overall)}
        />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center`}
              >
                <Icon className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {activeTab === 'overview' && <OverviewTab coverage={coverage} />}
        {activeTab === 'spatial' && <SpatialTab spatialInfo={coverage.spatialInfo} />}
        {activeTab === 'temporal' && <TemporalTab temporalInfo={coverage.temporalInfo} />}
        {activeTab === 'quality' && <QualityTab dataQuality={coverage.dataQuality} />}
        {activeTab === 'statistics' && <StatisticsTab statistics={coverage.statistics} />}
        {activeTab === 'metadata' && <MetadataTab metadata={coverage.metadata} />}
        {activeTab === 'related' && <RelatedDataTab relatedData={coverage.relatedData} />}
      </div>
    </div>
  );
}

// Status Badge Component
interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'pending' | 'payment_collection_expired';
}

function StatusBadge({ status }: StatusBadgeProps) {
  const getStatusStyles = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'payment_collection_expired':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-4 h-4" />;
      case 'inactive':
        return <XCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'payment_collection_expired':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyles(status)}`}>
      {getStatusIcon(status)}
      <span className="ml-2 capitalize">{status}</span>
    </span>
  );
}

// Quick Stat Card Component
interface QuickStatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  valueColor?: string;
}

function QuickStatCard({ label, value, icon, valueColor = 'text-gray-900' }: QuickStatCardProps) {
  return (
    <Card className="shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center">
          <div className="text-gray-400 mr-3">{icon}</div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
            <p className={`text-sm font-semibold ${valueColor} capitalize`}>
              {value.replace('_', ' ')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Overview Tab Component
function OverviewTab({ coverage }: { coverage: CoverageDetailView }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="text-lg font-semibold">Basic Information</h3>
        </CardHeader>
        <CardContent className="space-y-3">
          <InfoRow label="ID" value={coverage.basic.id} />
          <InfoRow label="Variable" value={coverage.basic.variable} />
          <InfoRow label="Created" value={new Date(coverage.basic.created_at).toLocaleString()} />
          <InfoRow label="Last Updated" value={new Date(coverage.basic.updated_at).toLocaleString()} />
          {coverage.basic.start_date && (
            <InfoRow label="Start Date" value={new Date(coverage.basic.start_date).toLocaleDateString()} />
          )}
          {coverage.basic.end_date && (
            <InfoRow label="End Date" value={new Date(coverage.basic.end_date).toLocaleDateString()} />
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="text-lg font-semibold">Data Quality Overview</h3>
        </CardHeader>
        <CardContent className="space-y-3">
          <InfoRow 
            label="Overall Quality" 
            value={coverage.dataQuality.overall}
            valueColor={getQualityColor(coverage.dataQuality.overall)}
          />
          <InfoRow 
            label="Completeness" 
            value={`${coverage.dataQuality.completeness}%`}
          />
          {coverage.dataQuality.accuracy && (
            <InfoRow 
              label="Accuracy" 
              value={`${coverage.dataQuality.accuracy}%`}
            />
          )}
          <InfoRow 
            label="Timeliness" 
            value={`${coverage.dataQuality.timeliness} hours ago`}
          />
          <InfoRow 
            label="Validation Status" 
            value={coverage.dataQuality.validationStatus.replace('_', ' ')}
            valueColor={coverage.dataQuality.validationStatus === 'validated' ? 'text-green-600' : 'text-yellow-600'}
          />
        </CardContent>
      </Card>
    </div>
  );
}

// Info Row Component
interface InfoRowProps {
  label: string;
  value: string;
  valueColor?: string;
}

function InfoRow({ label, value, valueColor = 'text-gray-900' }: InfoRowProps) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-gray-600">{label}:</span>
      <span className={`text-sm font-medium ${valueColor} capitalize`}>{value}</span>
    </div>
  );
}

// Utility function for quality color
function getQualityColor(quality: DataQualityLevel): string {
  switch (quality) {
    case 'excellent':
      return 'text-green-600';
    case 'good':
      return 'text-green-500';
    case 'fair':
      return 'text-yellow-600';
    case 'poor':
      return 'text-red-600';
    default:
      return 'text-gray-600';
  }
}

// Placeholder tab components (to be expanded)
function SpatialTab({ spatialInfo }: { spatialInfo: SpatialInformation }) {
  return (
    <Card className="shadow-lg">
      <CardHeader>
        <h3 className="text-lg font-semibold">Spatial Information</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {spatialInfo.bounds && (
            <InfoRow 
              label="Bounds" 
              value={`[${spatialInfo.bounds.map((b: number) => b.toFixed(4)).join(', ')}]`}
            />
          )}
          {spatialInfo.area && (
            <InfoRow label="Area" value={`${spatialInfo.area.toLocaleString()} km²`} />
          )}
          {spatialInfo.resolution && (
            <InfoRow 
              label="Resolution" 
              value={`${spatialInfo.resolution.value} ${spatialInfo.resolution.unit}`}
            />
          )}
          {spatialInfo.coordinateSystem && (
            <InfoRow label="Coordinate System" value={spatialInfo.coordinateSystem} />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TemporalTab({ temporalInfo }: { temporalInfo: TemporalInformation }) {
  return (
    <Card className="shadow-lg">
      <CardHeader>
        <h3 className="text-lg font-semibold">Temporal Information</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <InfoRow label="Start" value={new Date(temporalInfo.start).toLocaleString()} />
          {temporalInfo.end && (
            <InfoRow label="End" value={new Date(temporalInfo.end).toLocaleString()} />
          )}
          <InfoRow label="Resolution" value={temporalInfo.resolution} />
          <InfoRow label="Time Zone" value={temporalInfo.timeZone} />
          <InfoRow label="Data Points" value={temporalInfo.dataPoints.toLocaleString()} />
        </div>
      </CardContent>
    </Card>
  );
}

function QualityTab({ dataQuality }: { dataQuality: DataQualityInfo }) {
  return (
    <div className="space-y-6">
      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="text-lg font-semibold">Quality Metrics</h3>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label="Overall" value={dataQuality.overall} />
            <InfoRow label="Completeness" value={`${dataQuality.completeness}%`} />
            {dataQuality.accuracy && (
              <InfoRow label="Accuracy" value={`${dataQuality.accuracy}%`} />
            )}
            <InfoRow label="Consistency" value={`${dataQuality.consistency}%`} />
          </div>
        </CardContent>
      </Card>

      {dataQuality.issues && dataQuality.issues.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <h3 className="text-lg font-semibold">Quality Issues</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {dataQuality.issues.map((issue: DataQualityIssue, index: number) => (
                <div key={index} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-gray-900 capitalize">
                      {issue.type.replace('_', ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      issue.severity === 'critical' ? 'bg-red-100 text-red-800' :
                      issue.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                      issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-green-100 text-green-800'
                    }`}>
                      {issue.severity}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{issue.description}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StatisticsTab({ statistics }: { statistics: CoverageStatistics }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="text-lg font-semibold">Basic Statistics</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <InfoRow label="Minimum" value={statistics.basic.min.toFixed(4)} />
            <InfoRow label="Maximum" value={statistics.basic.max.toFixed(4)} />
            <InfoRow label="Mean" value={statistics.basic.mean.toFixed(4)} />
            <InfoRow label="Median" value={statistics.basic.median.toFixed(4)} />
            <InfoRow label="Std Deviation" value={statistics.basic.standardDeviation.toFixed(4)} />
            <InfoRow label="Variance" value={statistics.basic.variance.toFixed(4)} />
          </div>
        </CardContent>
      </Card>

      <Card className="shadow-lg">
        <CardHeader>
          <h3 className="text-lg font-semibold">Distribution</h3>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <InfoRow label="Q1" value={statistics.distribution.quartiles[0].toFixed(4)} />
            <InfoRow label="Q2 (Median)" value={statistics.distribution.quartiles[1].toFixed(4)} />
            <InfoRow label="Q3" value={statistics.distribution.quartiles[2].toFixed(4)} />
            {Object.entries(statistics.distribution.percentiles).map(([key, value]) => (
              <InfoRow key={key} label={key} value={value.toFixed(4)} />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MetadataTab({ metadata }: { metadata: CoverageMetadata }) {
  return (
    <Card className="shadow-lg">
      <CardHeader>
        <h3 className="text-lg font-semibold">Metadata</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <InfoRow label="Provider" value={metadata.provider} />
          <InfoRow label="License" value={metadata.license} />
          <InfoRow label="Version" value={metadata.version} />
          <InfoRow label="Last Processed" value={new Date(metadata.lastProcessed).toLocaleString()} />
          {metadata.citation && <InfoRow label="Citation" value={metadata.citation} />}
          {metadata.methodology && <InfoRow label="Methodology" value={metadata.methodology} />}
        </div>
      </CardContent>
    </Card>
  );
}

function RelatedDataTab({ relatedData }: { relatedData: RelatedDataInfo }) {
  return (
    <div className="space-y-6">
      {relatedData.dependencies && relatedData.dependencies.length > 0 && (
        <Card className="shadow-lg">
          <CardHeader>
            <h3 className="text-lg font-semibold">Dependencies</h3>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {relatedData.dependencies.map((dep: { id: string; name: string; type: string; relationship: string }, index: number) => (
                <div key={index} className="p-3 border border-gray-200 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-medium text-gray-900">{dep.name}</h4>
                      <p className="text-sm text-gray-600">{dep.relationship}</p>
                    </div>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                      {dep.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
} 